import React, {useState, useEffect, useCallback} from 'react'
import './../../App.css';
import './Reports.css';
import axios from 'axios';
import {token, domain} from '../Security';
import jsPDF from "jspdf";
import loadingSpinner from './../../assets/loading.gif';

function LoansMonthlyAmmortizationSummary({isHideSideMenu}) {
    const [classificationId, setClassificationId] = useState("");
    const [departmentId, setDepartmentId] = useState("");
    const [company, setCompany] = useState([]);
    const [locationId, setLocationId] = useState("");
    const [loanType, setLoanType] = useState("");
    const [errorState, setErrorState] = useState(false);
    const [loading, setLoading] = useState(false);

    const downloadPDF = useCallback(async (data) => {
        // Create a new jsPDF instance
        const _doc = new jsPDF("landscape");

        const drawLine = (doc, lineX, lineY) => {
            const startX = lineX;
            const endX = doc.internal.pageSize.getWidth() - 10; // Width of page minus some margin
            const startY = lineY;
            doc.line(startX, startY, endX, startY);
        };
        const title = (doc, title, titleX, titleY) =>{
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            // doc.setTextColor("#668fca");
            doc.setLineWidth(0.5);
            doc.text(title, titleX, titleY);
        }
        const header = (doc, title, titleX, titleY) =>{
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            // doc.setTextColor("#668fca");
            doc.setLineWidth(0.5);
            doc.text(title, titleX, titleY);
        }
        const info = (doc, label, labelX, labelY) =>{
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.text(label, labelX, labelY); 
        }
        const numberWithCommasAndDecimal = (number) => {
            return number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };
        const formatName = (name) => {
            if(name){
                const nameParts = name.split(", ");
                const formattedName = nameParts.map(part => {
                    return part.split('-').map(subPart => 
                        subPart.charAt(0).toUpperCase() + subPart.slice(1).toLowerCase()
                    ).join('-');
                }).join(", ");
    
                return formattedName.replace(/\b\w/g, (match) => match.toUpperCase());
            }
            return "";
        };
        function formatDate(dateStr) {
            const date = new Date(dateStr);
            const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
            const formattedDate = date.toLocaleDateString("en-US", options);
            return formattedDate;
        }
        
        // Add the image to the PDF
        const imgData = `data:image/png;base64,${company.logo}`;
        _doc.addImage(imgData, 'PNG', 110, 5, 70, 30);
        
        title(_doc, `Loans Monthly Ammortization Summary`, 95, 35);
        info(_doc, `Loan Type: ${loanType}`, 125, 40);

        drawLine(_doc, 10, 50);
        header(_doc, `Employee Id`, 10, 55);
        header(_doc, `Employee Name`, 50, 55);
        header(_doc, `Date Granted`, 120, 55);
        header(_doc, `Deduction`, 170, 55);
        header(_doc, `Principal Amount`, 210, 55);
        header(_doc, `Amortization`, 260, 55);
        drawLine(_doc, 10, 57.5);

        let y = 62.5; // Starting Y position for data
        const pageHeight = _doc.internal.pageSize.getHeight();
        const threshold = pageHeight - 20; 

        let totalDeduction = 0;
        let totalPrincipalAmount = 0;
        let totalAmmortization = 0;

        data.forEach((employee) => {
            if (y > threshold) {
                _doc.addPage(); // Add new page
                y = 20;
            }

            const deduction = 0;
            const principalAmount = 0;
            const amortization = 0;

            info(_doc, employee.employeeId, 11, y);
            info(_doc, formatName(employee.fullName), 50, y);
            info(_doc, formatDate(new Date()), 120, y);
            info(_doc, numberWithCommasAndDecimal(deduction), 170, y);
            info(_doc, numberWithCommasAndDecimal(principalAmount), 210, y);
            info(_doc, numberWithCommasAndDecimal(amortization), 260, y);

            totalDeduction += deduction;
            totalPrincipalAmount += principalAmount;
            totalAmmortization += amortization;
            
            y += 5; // Increment Y position for next row
        });

        drawLine(_doc, 10, y);
        header(_doc, `Grand Total:`, 120, y + 10);
        info(_doc, numberWithCommasAndDecimal(totalDeduction), 170, y + 10);
        info(_doc, numberWithCommasAndDecimal(totalPrincipalAmount), 210, y + 10);
        info(_doc, numberWithCommasAndDecimal(totalAmmortization), 260, y + 10);

        // Save the PDF
        _doc.save(`Loans_Monthly_Amortization_Summary.pdf`);
    }, [loanType, company]);

    const fetchData = useCallback(async () => {
        const apiUrl =  domain + "/api/Employee";

        try {
            setLoading(true);
            const response = await axios.get(apiUrl , {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json' // You can include other headers if needed
            }})
            
            const classificationIdNumber = Number(classificationId);
            const departmentIdNumber = Number(departmentId);
            const companyIdNumber = Number(company.id);
            const locationIdNumber = Number(locationId);

            const sortedData = response.data
                .filter(data => (data.employeeInformation?.classificationId ?? null) === classificationIdNumber)
                .filter(data => (data.employeeInformation?.departmentId ?? null) === departmentIdNumber)
                .filter(data => (data.employeeInformation?.companyId ?? null) === companyIdNumber)
                .filter(data => (data.employeeInformation?.locationId ?? null) === locationIdNumber);

            downloadPDF(sortedData);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [downloadPDF, classificationId, company, departmentId, locationId]);

    const generate = () =>{
        if(!classificationId
            || !departmentId
            || !company
            || !locationId
            || !loanType
        ){
            setErrorState(true);
            return;
        }
        setErrorState(false);
        fetchData();
    };

    return (
        <div>
            <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
                <div className="LeftSpace"></div>
                <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
                    <div className="ReportsInfo">
                        <div className="reports-header">
                            <div className='sub-title row'>
                                <span style={{padding:"0 5px 0 0", color:"#b0b0b0"}}>{"Reports >"} </span>
                                <span style={{borderBottom:"1px solid black"}}>{"Loans Monthly Amortization Summary"}</span>
                            </div>
                            <h3>Generate Loans Monthly Amortization Summary Report</h3>
                        </div>
                        <div className="reports-body">
                            <div className="info-item-holder row">
                                <div className="info-item3 info-item column">
                                    <div className="info-item-label" style={{fontWeight:"normal", fontSize:"14px"}}>Loan Type</div>
                                    <div className="info-item-input">
                                        <LoanType loanType={loanType} setLoanType={setLoanType}/>
                                    </div>
                                    <span style={{
                                                visibility: !loanType && errorState ? "visible" : "hidden",
                                                color:"#ff6565", 
                                                fontSize:"12px"}}>Please fill up this field
                                    </span>
                                </div>
                                <div className="info-item3 info-item column">
                                    <div className="info-item-label" style={{fontWeight:"normal", fontSize:"14px"}}>Classification</div>
                                    <div className="info-item-input">
                                        <ClassificationIdSelect classificationId={classificationId} setClassificationId={setClassificationId}/>
                                    </div>
                                    <span style={{
                                                visibility: !classificationId && errorState ? "visible" : "hidden",
                                                color:"#ff6565", 
                                                fontSize:"12px"}}>Please fill up this field
                                    </span>
                                </div>
                                <div className="info-item3 info-item column">
                                    <div className="info-item-label" style={{fontWeight:"normal", fontSize:"14px"}}>Department</div>
                                    <div className="info-item-input">
                                        <DepartmentIdSelect departmentId={departmentId} setDepartmentId={setDepartmentId}/>
                                    </div>
                                    <span style={{
                                                visibility: !departmentId && errorState ? "visible" : "hidden",
                                                color:"#ff6565", 
                                                fontSize:"12px"}}>Please fill up this field
                                    </span>
                                </div>
                            </div>
                            <div className="info-item-holder row">
                                <div className="info-item3 info-item column">
                                    <div className="info-item-label" style={{fontWeight:"normal", fontSize:"14px"}}>Company</div>
                                    <div className="info-item-input">
                                        <CompanyTypeSelect company={company} setCompany={setCompany}/>
                                    </div>
                                    <span style={{
                                                visibility: !company && errorState ? "visible" : "hidden",
                                                color:"#ff6565", 
                                                fontSize:"12px"}}>Please fill up this field
                                    </span>
                                </div>
                                <div className="info-item3 info-item column">
                                    <div className="info-item-label" style={{fontWeight:"normal", fontSize:"14px"}}>Location</div>
                                    <div className="info-item-input">
                                        <LocationIdSelect locationId={locationId} setLocationId={setLocationId}/>
                                    </div>
                                    <span style={{
                                                visibility: !locationId && errorState ? "visible" : "hidden",
                                                color:"#ff6565", 
                                                fontSize:"12px"}}>Please fill up this field
                                    </span>
                                </div>
                                <div className="info-item3 info-item column" style={{visibility:"hidden"}}>
                                    <div className="info-item-label" style={{fontWeight:"normal", fontSize:"14px"}}>Location</div>
                                    <div className="info-item-input">
                                        <LocationIdSelect locationId={locationId} setLocationId={setLocationId}/>
                                    </div>
                                    <span style={{
                                                visibility: !locationId && errorState ? "visible" : "hidden",
                                                color:"#ff6565", 
                                                fontSize:"12px"}}>Please fill up this field
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="reports-footer">
                            <div className="buttons row">
                                <button id="activeEmployeesGenerate"
                                    className="generate row" 
                                    onClick={generate}
                                    > GENERATE
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {loading ? (
                <div style={{ position:"fixed", top:"50%", left:"50%", transform:"translate(-50%, -50%)", zIndex:"3005"}}>
                    <img src={loadingSpinner} alt="Loading..." />
                </div>
            ) : (
                <></>
                )
            }
        </div>
    )
}

function ClassificationIdSelect({classificationId, setClassificationId}){
    const [isSelected, setIsSelected] = useState(false);

    const handleSelectChange = (event) => {
        setIsSelected(true);
        setClassificationId(event.target.value);
    };

    const [data, setData] = useState([]);

    const fetchData = async () => {
        const apiUrl =  domain + "/api/Classification/active";

        await axios.get(apiUrl , {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json' // You can include other headers if needed
        }})
        .then(response => {
            setData(response.data); // Set the response data to the state
        })
        .catch(error => {
            console.error('Error fetching data:', error);
        });
    }

    useEffect(() => {
        fetchData();
        if(classificationId !== "" && classificationId !== 0){
            setIsSelected(true);
        } else {
            setIsSelected(false);
        }
    }, [classificationId]);

    return (
        <div>
            <select id="employee-select-PagibigReport" value={classificationId} onChange={handleSelectChange}style={{ color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)" }}>
            <option value="" disabled hidden>Choose classification</option>
            {data.map((classificationId) => ( 
                SetClassificationId(classificationId)
            ))}
            </select>
        </div>
    );
};
function SetClassificationId(classificationId){
    return(
        <option value={classificationId.id}>{
            classificationId.description}
        </option>
    )
}
function DepartmentIdSelect({departmentId, setDepartmentId}){
    const [isSelected, setIsSelected] = useState(false);

    const handleSelectChange = (event) => {
        setIsSelected(true);
        setDepartmentId(event.target.value);
    };

    const [data, setData] = useState([]);

    const fetchData = async () => {
        const apiUrl =  domain + "/api/Department/active";

        await axios.get(apiUrl , {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json' // You can include other headers if needed
        }})
        .then(response => {
            response.data.sort((a, b) => a.description.localeCompare(b.description));
            setData(response.data); // Set the response data to the state
        })
        .catch(error => {
            console.error('Error fetching data:', error);
        });
    }

    useEffect(() => {
        fetchData();
        if(departmentId !== "" && departmentId !== 0){
            setIsSelected(true);
        } else {
            setIsSelected(false);
        }
    }, [departmentId]);

    return (
        <div>
            <select id="employee-select-PagibigReport" value={departmentId} onChange={handleSelectChange}style={{ color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)" }}>
            <option value="" disabled hidden>Choose department</option>
            {data.map((departmentId) => ( 
                SetDepartmentId(departmentId)
            ))}
            </select>
        </div>
    );
    };
function SetDepartmentId(departmentId){
    return(
        <option value={departmentId.id}>{
            departmentId.description}
        </option>
    )
}
function CompanyTypeSelect({company, setCompany}){
    const [isSelected, setIsSelected] = useState(false);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const selectedCompanyId = event.target.value; // Get the selected company ID
        const selectedCompany = data.find((comp) => comp.id.toString() === selectedCompanyId.toString()); // Find the full company object

        if (selectedCompany) {
            setCompany(selectedCompany); // Set the full company object
            setIsSelected(true);
        }
    };

    const [data, setData] = useState([]);

    const fetchData = async () => {
        const apiUrl =  domain + "/api/Company/active";

        await axios.get(apiUrl , {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json' // You can include other headers if needed
        }})
        .then(response => {
            response.data.sort((a, b) => a.name.localeCompare(b.name));
            setData(response.data); // Set the response data to the state
        })
        .catch(error => {
            console.error('Error fetching data:', error);
        });
    }

    useEffect(() => {
        fetchData();
        if (company !== "" && company !== 0 && company !== null && company !== undefined && company.length !== 0) {
            setIsSelected(true);
        } else {
            setIsSelected(false);
        }
    }, [company]);

    return (
        <div>
            <select id="employee-select-PagibigReport" value={company} onChange={handleSelectChange}style={{ color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)" }}>
            <option value="" disabled hidden>Choose company type</option>
            {data.map((company) => ( 
                SetCompanyType(company)
            ))}
            </select>
        </div>
    );
};
function SetCompanyType(company){
    return(
        <option value={company.id}>{
            company.name}
        </option>
    )

}
function LocationIdSelect({locationId, setLocationId}){
    const [isSelected, setIsSelected] = useState(false);

    const handleSelectChange = (event) => {
        setIsSelected(true);
        setLocationId(event.target.value);
    };

    const [data, setData] = useState([]);

    const fetchData = async () => {
        const apiUrl =  domain + "/api/Location/active";

        await axios.get(apiUrl , {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json' // You can include other headers if needed
        }})
        .then(response => {
            response.data.sort((a, b) => a.description.localeCompare(b.description));
            setData(response.data); // Set the response data to the state
        })
        .catch(error => {
            console.error('Error fetching data:', error);
        });
    }

    useEffect(() => {
        fetchData();
        if(locationId !== "" && locationId !== 0){
            setIsSelected(true);
        } else {
            setIsSelected(false);
        }
    }, [locationId]);

    return (
        <div>
            <select id="employee-select-PagibigReport" value={locationId} onChange={handleSelectChange}style={{ color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)" }}>
            <option value="" disabled hidden>Choose location</option>
            {data.map((locationId) => ( 
                SetLocationId(locationId)
            ))}
            </select>
        </div>
    );
};
function SetLocationId(locationId){
    return(
        <option value={locationId.id}>{
            locationId.description}
        </option>
    )

}
function LoanType({loanType, setLoanType}){
    const [isSelected, setIsSelected] = useState(false);
    
    const handleSelectChange = (event) => {
        setLoanType(event.target.value);
        setIsSelected(true);
    };

    const loans = [
        "SSS Loan", "Pagibig Loan", "Company Loan",
    ];
    
    useEffect(() => {
        if(loanType !== "" && loanType !== 0){
            setIsSelected(true);
        } else {
            setIsSelected(false);
        }
    }, [loanType]);

    return (
        <div>
            <select id="monthSSSReport-select" value={loanType} onChange={handleSelectChange} style={{ color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)" }}>
                <option value="" disabled hidden>Choose a loan type</option>
                {loans.map((loanName, index) => (
                    <option key={index} value={loanName}>{loanName}</option>
                ))}
            </select>
        </div>
    );
};

export default LoansMonthlyAmmortizationSummary
